import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../api";
import { useCurrentUser } from "../../auth";
import * as P from "../../constants/permissions";
import {
  Alert,
  Button,
  Empty,
  Field,
  Input,
  Loading,
  Status,
} from "../../components/ui";
import { Feedback, LoadError, useAction, useResource } from "./shared";

type Plan = {
  id?: string;
  name: string;
  limits: Record<string, number | null>;
  price_unit_amount?: number | null;
  price_currency?: string | null;
  active?: boolean;
  type?: string;
};
type Pack = {
  id: string;
  name: string;
  action_code: string;
  units: number;
  price_unit_amount: number | null;
  price_currency: string | null;
};
type Actions = Record<string, { available: number; avg_monthly_3m: number }>;
type Resources = Record<
  string,
  { available: number; used: number; limit: number | null }
>;
export default function BillingSettings() {
  const { t, i18n } = useTranslation("settings");
  const api = useApi();
  const { permissions, reloadCurrentUser } = useCurrentUser();
  const can = (p: string) => permissions.includes(p);
  const plans = useResource<Plan[]>("/billing/subscriptions");
  const packs = useResource<Pack[]>("/billing/action-packs");
  const usage = useResource<Actions>(
    can(P.STATS_USAGE_PERMISSION) ? "/stats/actions-usage" : null,
  );
  const resources = useResource<Resources>(
    can(P.STATS_USAGE_PERMISSION) ? "/stats/resources-usage" : null,
  );
  const action = useAction();
  const [tool, setTool] = useState<"credits" | "subscription" | null>(null);
  const manualInfo = useResource<{
    plan_name: string;
    limits: Record<string, number | null>;
  }>(
    tool === "subscription" && can(P.BILLING_SUBSCRIPTION_MANUAL_PERMISSION)
      ? "/billing/manual-subscription-info"
      : null,
  );
  const [units, setUnits] = useState("");
  const [from, setFrom] = useState("");
  const [until, setUntil] = useState("");
  const price = (amount?: number | null, currency?: string | null) =>
    amount == null || !currency
      ? t("billing.priceUnavailable")
      : new Intl.NumberFormat(i18n.language, {
          style: "currency",
          currency,
        }).format(amount / 100);
  const resourceName = (code: string) =>
    t(
      code === "PRODUCT"
        ? "billing.products"
        : code === "USER"
          ? "billing.users"
          : code === "PRINTER"
            ? "billing.printers"
            : "billing.resources",
    );
  const actionName = (code: string) =>
    t(
      code === "TRANSLATION_REQUEST"
        ? "billing.translationCredits"
        : "billing.actionCredits",
    );
  const checkout = (path: string, payload: object, field: string) =>
    void action.run(async () => {
      const result = await api.post(path, payload);
      if (!result?.[field]) throw new Error("Missing redirect");
      const destination = new URL(result[field]);
      if (!["https:", "http:"].includes(destination.protocol))
        throw new Error("Invalid redirect");
      window.location.assign(destination.href);
    });
  const grant = (event: FormEvent) => {
    event.preventDefault();
    const count = Number(units);
    if (!Number.isSafeInteger(count) || count < 1) return;
    void action.run(
      async () => {
        await api.post("/organizations/me/grants", {
          action_code: "TRANSLATION_REQUEST",
          units: count,
        });
        setUnits("");
        setTool(null);
        await usage.reload();
      },
      t("billing.granted", { count }),
    );
  };
  const addSubscription = (event: FormEvent) => {
    event.preventDefault();
    if (!manualInfo.data) return;
    if (!from || !until || new Date(until) <= new Date(from)) {
      action.setError(t("billing.invalidDates"));
      return;
    }
    void action.run(async () => {
      await api.post("/organizations/me/subscriptions/manual", {
        active_from: new Date(from).toISOString(),
        active_to: new Date(until).toISOString(),
      });
      setTool(null);
      await plans.reload();
      await resources.reload();
      await reloadCurrentUser();
    }, t("billing.subscriptionAdded"));
  };
  const active = plans.data?.find((plan) => plan.active);
  return (
    <section className="settings-section">
      <div className="settings-section-heading">
        <h2>{t("sections.billing")}</h2>
        {can(P.BILLING_MANAGE_PERMISSION) && (
          <Button
            variant={tool ? "outline" : "default"}
            disabled={action.busy}
            onClick={() =>
              checkout("/billing/customer-portal", {}, "portal_url")
            }
          >
            {t("billing.manage")}
          </Button>
        )}
      </div>
      <Feedback {...action} />
      <LoadError {...plans} />
      <LoadError {...packs} />
      <LoadError {...usage} />
      <LoadError {...resources} />
      {plans.loading && !plans.data && <Loading label={t("common.loading")} />}
      {active && (
        <div className="settings-summary">
          <span className="settings-muted">{t("billing.currentPlan")}</span>
          <strong>
            {active.type === "trial" ? t("billing.trial") : active.name}
          </strong>
          <Status tone="success">{t("billing.active")}</Status>
        </div>
      )}
      {can(P.STATS_USAGE_PERMISSION) && (
        <div className="settings-usage">
          {((usage.loading && !usage.data) ||
            (resources.loading && !resources.data)) && (
            <Loading label={t("common.loading")} />
          )}
          {Object.entries(usage.data || {}).map(([code, value]) => (
            <div key={code} className="settings-usage-row">
              <span>
                {actionName(code)}
                <small>{t("billing.remaining")}</small>
              </span>
              <strong>
                {new Intl.NumberFormat(i18n.language).format(value.available)}
              </strong>
            </div>
          ))}
          {Object.entries(resources.data || {}).map(([code, value]) => (
            <div key={code} className="settings-usage-row">
              <span>
                {resourceName(code)}
                <small>{t("billing.usedCapacity")}</small>
              </span>
              <strong>
                {value.used}{" "}
                <span className="settings-muted">
                  /{" "}
                  {value.limit == null || value.limit < 0
                    ? t("billing.unlimited")
                    : value.limit}
                </span>
              </strong>
            </div>
          ))}
          {usage.data &&
            resources.data &&
            !Object.keys(usage.data).length &&
            !Object.keys(resources.data).length && (
              <p className="settings-muted">{t("billing.noUsage")}</p>
            )}
        </div>
      )}
      <h3>{t("billing.plans")}</h3>
      {plans.data?.length ? (
        <div className="settings-offers">
          {plans.data.map((plan, index) => (
            <div
              key={plan.id || `${plan.name}-${index}`}
              className="settings-offer"
            >
              <div>
                <strong>
                  {plan.type === "trial" ? t("billing.trial") : plan.name}
                </strong>
                <p className="settings-muted">
                  {Object.entries(plan.limits || {})
                    .map(
                      ([code, limit]) =>
                        `${resourceName(code)}: ${limit == null || limit < 0 ? t("billing.unlimited") : limit}`,
                    )
                    .join(" · ")}
                </p>
              </div>
              <span className="settings-price">
                {price(plan.price_unit_amount, plan.price_currency)}
              </span>
              {plan.active ? (
                <Status tone="success">{t("billing.current")}</Status>
              ) : can(P.BILLING_PURCHASE_SUBSCRIPTION_PERMISSION) &&
                plan.id &&
                plan.price_unit_amount != null ? (
                <Button
                  variant="outline"
                  disabled={action.busy}
                  onClick={() =>
                    checkout(
                      "/billing/choose-subscription",
                      { id: plan.id },
                      "checkout_url",
                    )
                  }
                >
                  {t("billing.choose")}
                </Button>
              ) : (
                <span className="settings-muted">
                  {t(
                    can(P.BILLING_PURCHASE_SUBSCRIPTION_PERMISSION)
                      ? "billing.unavailableHere"
                      : "billing.askAdmin",
                  )}
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        !plans.loading &&
        !plans.error && (
          <Empty
            title={t("billing.noPlans")}
            description={t("billing.askAdmin")}
          />
        )
      )}
      <h3>{t("billing.packs")}</h3>
      {packs.data?.length ? (
        <div className="settings-offers">
          {packs.data.map((pack) => (
            <div className="settings-offer" key={pack.id}>
              <div>
                <strong>{pack.name}</strong>
                <p className="settings-muted">
                  {pack.units}{" "}
                  {actionName(pack.action_code).toLocaleLowerCase(
                    i18n.language,
                  )}
                </p>
              </div>
              <span className="settings-price">
                {price(pack.price_unit_amount, pack.price_currency)}
              </span>
              {can(P.BILLING_PURCHASE_PACK_PERMISSION) && (
                <Button
                  variant="outline"
                  disabled={action.busy || pack.price_unit_amount == null}
                  onClick={() =>
                    checkout(
                      "/billing/buy-pack",
                      { id: pack.id },
                      "checkout_url",
                    )
                  }
                >
                  {t("billing.buy")}
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : (
        !packs.loading &&
        !packs.error && <p className="settings-muted">{t("billing.noPacks")}</p>
      )}
      {(can(P.BILLING_GRANT_PERMISSION) ||
        can(P.BILLING_SUBSCRIPTION_MANUAL_PERMISSION)) && (
        <div className="settings-admin-tools">
          <h3>{t("billing.adminTools")}</h3>
          <div className="settings-inline">
            {can(P.BILLING_GRANT_PERMISSION) && (
              <Button
                variant="outline"
                disabled={action.busy}
                onClick={() => setTool(tool === "credits" ? null : "credits")}
              >
                {t("billing.addCredits")}
              </Button>
            )}
            {can(P.BILLING_SUBSCRIPTION_MANUAL_PERMISSION) && (
              <Button
                variant="outline"
                disabled={action.busy}
                onClick={() =>
                  setTool(tool === "subscription" ? null : "subscription")
                }
              >
                {t("billing.addSubscription")}
              </Button>
            )}
          </div>
          {tool === "credits" && (
            <form
              className="settings-form settings-inline-form settings-form-width"
              onSubmit={grant}
            >
              <Field
                htmlFor="credit-units"
                label={t("billing.translationCredits")}
                hint={t("billing.grantHint")}
              >
                <Input
                  id="credit-units"
                  type="number"
                  min="1"
                  step="1"
                  max="1000000"
                  value={units}
                  onChange={(e) => setUnits(e.target.value)}
                  required
                  autoFocus
                  disabled={action.busy}
                />
              </Field>
              <div className="settings-inline">
                <Button
                  type="submit"
                  disabled={
                    action.busy ||
                    !Number.isSafeInteger(Number(units)) ||
                    Number(units) < 1
                  }
                >
                  {t("billing.addCredits")}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setTool(null)}
                  disabled={action.busy}
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </form>
          )}
          {tool === "subscription" && (
            <form
              className="settings-form settings-inline-form settings-form-width"
              onSubmit={addSubscription}
            >
              <h3>{t("billing.manualPlan")}</h3>
              <p className="settings-muted">{t("billing.manualEffect")}</p>
              <LoadError {...manualInfo} />
              {manualInfo.loading && <Loading label={t("common.loading")} />}
              {manualInfo.data && (
                <p>
                  {Object.entries(manualInfo.data.limits)
                    .map(
                      ([code, limit]) =>
                        `${resourceName(code)}: ${limit == null || limit < 0 ? t("billing.unlimited") : limit}`,
                    )
                    .join(" · ")}
                </p>
              )}
              <Field
                htmlFor="subscription-from"
                label={t("billing.activeFrom")}
                hint={t("billing.localDates")}
              >
                <Input
                  id="subscription-from"
                  type="datetime-local"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  required
                  disabled={action.busy}
                />
              </Field>
              <Field
                htmlFor="subscription-until"
                label={t("billing.activeUntil")}
              >
                <Input
                  id="subscription-until"
                  type="datetime-local"
                  value={until}
                  onChange={(e) => setUntil(e.target.value)}
                  required
                  disabled={action.busy}
                />
              </Field>
              <div className="settings-inline">
                <Button
                  type="submit"
                  disabled={action.busy || !from || !until || !manualInfo.data}
                >
                  {t("billing.addSubscription")}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setTool(null)}
                  disabled={action.busy}
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
      {!can(P.BILLING_MANAGE_PERMISSION) &&
        !can(P.BILLING_PURCHASE_PACK_PERMISSION) &&
        !can(P.BILLING_PURCHASE_SUBSCRIPTION_PERMISSION) && (
          <Alert variant="info">{t("billing.askAdmin")}</Alert>
        )}
    </section>
  );
}
